import {
  VStack,
  StackDivider,
  Flex,
  Heading,
  Button,
  Icon,
  Text,
  Box,
  HStack,
} from '@chakra-ui/react';
import { AiOutlineShop } from 'react-icons/ai';
import { IoPersonCircleOutline } from 'react-icons/io5';

import Item from './Item';

import * as fp from 'lodash/fp';

import memberInfoAtom from '~/recoil/atom/memberInfo';
import isHostSelector from '~/recoil/selectors/isHost';
import { useRecoilValue } from 'recoil';

import { StoreOrderInCart, CartType } from '~/types/cart';
import { StoreOrderInPlatform } from '~/types/order';

type StoreItemsProps = {
  order?: StoreOrderInCart | StoreOrderInPlatform;
  isInvalid?: boolean;
  cartType?: CartType;
  allowEdit?: boolean;
  onEdit?: (storeId: string, itemId: string, keyId: string) => void;
  onDelete?: (storeId: string, keyId: string) => void;
  onStoreDetail?: (storeId: string) => void;
};

export default function StoreItems({
  order = { storeId: '', name: '', items: [], groupItems: [] },
  isInvalid = false,
  allowEdit = false,
  cartType = CartType.CART_PERSONAL,
  onEdit,
  onDelete,
  onStoreDetail,
}: StoreItemsProps) {
  const isHost = useRecoilValue(isHostSelector);
  const memberInfo = useRecoilValue(memberInfoAtom);

  return (
    <VStack
      align="stretch"
      borderRadius="5px"
      borderWidth="1px"
      borderColor={isInvalid ? 'danger.600' : 'gray.400'}
      overflow="hidden"
      spacing={0}
      divider={<StackDivider borderColor="gray.400" />}
    >
      <Flex justify="space-between" align="center" p={2} bgColor="gray.100">
        <Heading size="xs">{order.name}</Heading>
        {onStoreDetail && (
          <Button
            size="sm"
            rightIcon={<Icon as={AiOutlineShop} boxSize="1.25rem" />}
            bgColor="gray.200"
            color="fontColor"
            _hover={{
              bgColor: 'gray.200',
              color: 'fontColor',
            }}
            onClick={() => onStoreDetail(order.storeId)}
          >
            餐廳詳情
          </Button>
        )}
      </Flex>

      {fp.eq(cartType, CartType.CART_PERSONAL) && (
        <VStack
          align="stretch"
          overflow="hidden"
          divider={<StackDivider borderColor="gray.400" />}
          spacing={3}
          py={3}
        >
          {order.items.map((item) => (
            <Box key={item.keyId} px={2}>
              <Item
                item={item}
                allowEdit={allowEdit}
                onEdit={() => onEdit?.(order.storeId, item.id, item.keyId)}
                onDelete={() => onDelete?.(order.storeId, item.keyId)}
              />
            </Box>
          ))}
        </VStack>
      )}

      {fp.eq(cartType, CartType.CART_GROUP) && (
        <VStack
          spacing={3}
          py={3}
          align="stretch"
          overflow="hidden"
          divider={<StackDivider borderColor="dark" borderWidth="1px" />}
        >
          {order.groupItems.map(({ memberId, memberName, items }) => (
            <VStack
              key={memberId}
              px={2}
              align="stretch"
              spacing={1}
              divider={<StackDivider borderColor="gray.200" />}
              opacity={isHost || fp.eq(memberId, memberInfo.id) ? 1 : 0.5}
            >
              <HStack align="center">
                <Icon as={IoPersonCircleOutline} boxSize="1.5rem" />
                <Text fontWeight="bold" fontSize="sm">
                  {memberName}
                </Text>
              </HStack>
              <VStack spacing={3} align="stretch" divider={<StackDivider borderColor="gray.400" />}>
                {items.map((item) => (
                  <Item
                    key={item.keyId}
                    item={item}
                    allowEdit={allowEdit && (isHost || fp.eq(memberInfo.id, memberId))}
                    onEdit={() => onEdit?.(order.storeId, item.id, item.keyId)}
                    onDelete={() => onDelete?.(order.storeId, item.keyId)}
                  />
                ))}
              </VStack>
            </VStack>
          ))}
        </VStack>
      )}
    </VStack>
  );
}
